import { useEffect, useState, type FormEvent } from "react";

import { ApiError } from "@/api/client";
import { useAuthStore } from "@/auth/authStore";
import { hasGlobalRole } from "@/auth/permissions";
import type { AuthSessionContext, TokenPayload } from "@/auth/sessionTypes";
import { useLanguage } from "@/i18n/LanguageContext";

import { changePassword, fetchMyConferenceContext } from "./api";
import { PageHeader } from "./shells/PageHeader";

function buildSession(payload: TokenPayload): AuthSessionContext {
  return (
    payload.session ?? {
      user: payload.user,
      global_roles: [],
      member_profile: null,
      journal_memberships: [],
    }
  );
}

export function AccountSecurityPage() {
  const { t } = useLanguage();
  const globalRoles = useAuthStore((s) => s.globalRoles);
  const refreshToken = useAuthStore((s) => s.refreshToken);
  const setTokens = useAuthStore((s) => s.setTokens);
  const setSessionContext = useAuthStore((s) => s.setSessionContext);
  const isAdmin = hasGlobalRole(globalRoles, "admin");

  const [backTo, setBackTo] = useState(isAdmin ? "/admin" : "/dashboard");
  const [backLabel, setBackLabel] = useState(isAdmin ? t.nav.admin : t.nav.dashboard);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isAdmin) {
      setBackTo("/admin");
      setBackLabel(t.nav.admin);
      return;
    }
    fetchMyConferenceContext()
      .then((ctx) => {
        if (ctx.is_reviewer && !ctx.has_registration) {
          setBackTo("/reviews");
          setBackLabel(t.nav.reviews);
        } else {
          setBackTo("/dashboard");
          setBackLabel(t.nav.dashboard);
        }
      })
      .catch(() => {
        setBackTo("/dashboard");
        setBackLabel(t.nav.dashboard);
      });
  }, [isAdmin, t.nav.admin, t.nav.dashboard, t.nav.reviews]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setOk(null);
    if (newPassword !== confirmPassword) {
      setError(t.accountSecurity.mismatch);
      setSubmitting(false);
      return;
    }
    try {
      const json = await changePassword({
        current_password: currentPassword,
        new_password: newPassword,
        new_password_confirm: confirmPassword,
        refresh_token: refreshToken ?? undefined,
      });
      setTokens({
        accessToken: json.access_token,
        refreshToken: json.refresh_token,
      });
      setSessionContext(buildSession(json));
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setOk(json.message || t.accountSecurity.success);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t.common.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ maxWidth: 480 }}>
      <PageHeader
        title={t.accountSecurity.title}
        lead={t.accountSecurity.lead}
        backTo={backTo}
        backLabel={backLabel}
      />
      {error && <div className="dn-alert dn-alert-error">{error}</div>}
      {ok && <div className="dn-alert dn-alert-ok">{ok}</div>}
      <form className="dn-form" onSubmit={onSubmit}>
        <label>
          {t.accountSecurity.currentPassword}
          <input
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            type="password"
            required
            autoComplete="current-password"
          />
        </label>
        <label>
          {t.accountSecurity.newPassword}
          <input
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            type="password"
            required
            minLength={8}
            autoComplete="new-password"
          />
        </label>
        <p className="dn-muted" style={{ marginTop: "-0.5rem", fontSize: "0.9rem" }}>
          {t.accountSecurity.policyHint}
        </p>
        <label>
          {t.accountSecurity.confirmPassword}
          <input
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            type="password"
            required
            minLength={8}
            autoComplete="new-password"
          />
        </label>
        <button className="dn-btn dn-btn-primary" disabled={submitting} type="submit">
          {t.accountSecurity.submit}
        </button>
      </form>
    </div>
  );
}
